(() => {
  'use strict';

  const initGemGlowProduct = (root = document) => {
    root.querySelectorAll('.gg-product-page').forEach((section) => {
      if (section.dataset.ggInitialized === 'true') return;
      section.dataset.ggInitialized = 'true';

      initPincode(section);
      initMobileAccordions(section);
      initGalleryCounter(section);
      initStickyCart(section);
    });
  };

  const initPincode = (section) => {
    const wrapper = section.querySelector('[data-gg-delivery]');
    if (!wrapper) return;

    const input = wrapper.querySelector('.gg-delivery__input');
    const button = wrapper.querySelector('[data-gg-pincode-check]');
    const message = wrapper.querySelector('.gg-delivery__message');

    if (!input || !button || !message) return;

    try {
      const savedPincode = window.localStorage.getItem('gemglow_pincode');

      if (savedPincode && /^\d{6}$/.test(savedPincode)) {
        input.value = savedPincode;
      }
    } catch (error) {
      // Continue without localStorage.
    }

    const validate = () => {
      const value = input.value.replace(/\D/g, '').slice(0, 6);
      input.value = value;

      const valid = /^\d{6}$/.test(value);

      input.classList.toggle('is-invalid', !valid);
      message.classList.toggle('is-error', !valid);

      if (!valid) {
        message.textContent =
          'Please enter a valid 6-digit Indian pincode.';
        input.focus();
        return;
      }

      try {
        window.localStorage.setItem('gemglow_pincode', value);
      } catch (error) {
        // Continue without localStorage.
      }

      message.textContent =
        'Pincode saved. Exact delivery date and availability will be confirmed at checkout.';
    };

    input.addEventListener('input', () => {
      input.value = input.value.replace(/\D/g, '').slice(0, 6);
      input.classList.remove('is-invalid');
      message.classList.remove('is-error');
      message.textContent = '';
    });

    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        validate();
      }
    });

    button.addEventListener('click', validate);
  };

  const initMobileAccordions = (section) => {
    const tabs = section.querySelectorAll('.gg-tab');

    if (!tabs.length) return;

    if (window.matchMedia('(max-width: 749px)').matches) {
      tabs.forEach((tab) => {
        tab.removeAttribute('open');
      });
    }
  };

  const initGalleryCounter = (section) => {
    const mediaList = section.querySelector('.product__media-list');

    if (!mediaList) return;

    const getItems = () =>
      [...mediaList.querySelectorAll('.product__media-item')].filter(
        (item) => !item.hasAttribute('hidden')
      );

    const items = getItems();

    if (items.length < 2) return;

    const mediaWrapper = section.querySelector('.gg-product-media');

    if (!mediaWrapper) return;

    let counter = mediaWrapper.querySelector('.gg-gallery-count');

    if (!counter) {
      counter = document.createElement('span');
      counter.className = 'gg-gallery-count';
      counter.setAttribute('aria-live', 'polite');

      mediaWrapper.style.position = 'relative';
      mediaWrapper.appendChild(counter);
    }

    const updateCounter = () => {
      const currentItems = getItems();

      if (!currentItems.length) return;

      const viewportCenter = window.innerWidth / 2;

      let activeIndex = currentItems.findIndex((item) => {
        const rect = item.getBoundingClientRect();

        return (
          rect.left <= viewportCenter &&
          rect.right >= viewportCenter
        );
      });

      if (activeIndex < 0) activeIndex = 0;

      counter.textContent =
        `${activeIndex + 1} / ${currentItems.length}`;
    };

    updateCounter();

    mediaList.addEventListener('scroll', updateCounter, {
      passive: true
    });

    document.addEventListener('variant:change', () => {
      window.setTimeout(updateCounter, 80);
    });
  };

  const initStickyCart = (section) => {
    const sticky = section.querySelector(
      '[data-gg-sticky-cart]'
    );

    const stickyButton = section.querySelector(
      '[data-gg-sticky-submit]'
    );

    const stickyPrice = section.querySelector(
      '[data-gg-sticky-price]'
    );

    const mainButton = section.querySelector(
      '.product-form__submit'
    );

    const priceContainer = section.querySelector(
      '[id^="price-"]'
    );

    if (!sticky || !stickyButton || !mainButton) return;

    sticky.hidden = false;

    const syncState = () => {
      const disabled =
        mainButton.disabled ||
        mainButton.getAttribute('aria-disabled') === 'true';

      stickyButton.disabled = disabled;

      const mainButtonLabel =
        mainButton
          .querySelector('span')
          ?.textContent
          ?.trim() ||
        mainButton.textContent.trim();

      stickyButton.textContent =
        mainButtonLabel || 'Add to Cart';

      const visiblePrice = priceContainer?.querySelector(
        '.price-item--sale, ' +
        '.price__regular .price-item--regular, ' +
        '.price-item--regular'
      );

      if (visiblePrice && stickyPrice) {
        stickyPrice.textContent =
          visiblePrice.textContent.trim();
      }
    };

    syncState();

    const buttonObserver = new MutationObserver(syncState);

    buttonObserver.observe(mainButton, {
      attributes: true,
      childList: true,
      subtree: true,
      characterData: true
    });

    if (priceContainer) {
      const priceObserver = new MutationObserver(syncState);

      priceObserver.observe(priceContainer, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }

    if ('IntersectionObserver' in window) {
      const visibilityObserver = new IntersectionObserver(
        ([entry]) => {
          const isMobile = window.matchMedia(
            '(max-width: 749px)'
          ).matches;

          sticky.classList.toggle(
            'is-visible',
            isMobile && !entry.isIntersecting
          );
        },
        {
          threshold: 0.15
        }
      );

      visibilityObserver.observe(mainButton);
    }

    stickyButton.addEventListener('click', () => {
      syncState();

      if (stickyButton.disabled) {
        const picker = section.querySelector(
          'variant-selects, ' +
          'variant-radios, ' +
          '.product-form__input'
        );

        picker?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });

        return;
      }

      mainButton.click();
    });

    window.addEventListener(
      'resize',
      () => {
        const isMobile = window.matchMedia(
          '(max-width: 749px)'
        ).matches;

        if (!isMobile) {
          sticky.classList.remove('is-visible');
        }
      },
      {
        passive: true
      }
    );
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initGemGlowProduct();
      }
    );
  } else {
    initGemGlowProduct();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initGemGlowProduct(event.target);
    }
  );
})();